import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSidebar } from '../../contexts/SidebarContext';
import HeaderNavigation from '../../components/ui/HeaderNavigation';
import MainSidebar from '../../components/ui/MainSidebar';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import NotificationToast from '../../components/ui/NotificationToast';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import InvoiceTable from './components/InvoiceTable';
import InvoiceFilters from './components/InvoiceFilters';
import BillingAnalytics from './components/BillingAnalytics';
import CreateInvoiceModal from './components/CreateInvoiceModal';
import InvoiceDetailsModal from './components/InvoiceDetailsModal';
import InvoicePreview from './components/InvoicePreview';
import invoiceService from '../../services/invoiceService';

const InvoiceBillingManagement = () => {
  const { user, userProfile } = useAuth();
  const { isCollapsed } = useSidebar();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeView, setActiveView] = useState('invoices');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    client: '',
    status: '',
    dateFrom: '',
    dateTo: '',
    amountFrom: '',
    amountTo: ''
  });

  // Transform database invoice to component format
  const transformInvoice = (invoice) => {
    // Parse services from database (stored as JSON string)
    let services = [];
    if (invoice.services) {
      try {
        const parsedServices = typeof invoice.services === 'string' 
          ? JSON.parse(invoice.services) 
          : invoice.services;
        
        if (Array.isArray(parsedServices) && parsedServices.length > 0) {
          services = parsedServices.map(s => ({
            description: s.description || '',
            sacCode: s.sacCode || s.sac_code || '',
            quantity: parseFloat(s.quantity) || 1,
            rate: parseFloat(s.rate) || 0,
            amount: parseFloat(s.amount) || (parseFloat(s.rate) || 0) * (parseFloat(s.quantity) || 1)
          }));
        }
      } catch (e) {
        console.warn('Failed to parse services JSON:', e);
      }
    }
    
    // If no services found, create default from invoice data
    if (services.length === 0) {
      services = [{
        description: invoice.description || 'Service',
        sacCode: invoice.sac_code || '',
        quantity: 1,
        rate: parseFloat(invoice.amount || 0),
        amount: parseFloat(invoice.amount || 0)
      }];
    }

    return {
      id: invoice.id,
      invoiceNumber: invoice.invoice_number,
      clientName: invoice.client_name,
      clientEmail: invoice.client_email,
      clientGst: invoice.client_gst || '',
      totalAmount: parseFloat(invoice.total_amount || 0),
      gstAmount: parseFloat(invoice.tax_amount || 0),
      invoiceDate: invoice.issue_date || invoice.created_at?.split('T')[0] || '',
      dueDate: invoice.due_date || '',
      status: invoice.status || 'draft',
      sacCode: invoice.sac_code || '',
      description: invoice.description || '',
      amount: parseFloat(invoice.amount || 0),
      services: services,
      creatorName: invoice.creator_name || '',
      creatorEmail: invoice.creator_email || ''
    };
  };

  // Load invoices from API
  const loadInvoices = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build filter params
      const filterParams = {};
      if (filters.search) filterParams.search = filters.search;
      if (filters.status && filters.status !== 'all') filterParams.status = filters.status;
      if (filters.dateFrom) filterParams.date_from = filters.dateFrom;
      if (filters.dateTo) filterParams.date_to = filters.dateTo;

      const result = await invoiceService.getInvoices(filterParams);
      
      if (result.success && result.data) {
        const transformed = result.data.map(transformInvoice);
        setInvoices(transformed);
        applyFilters(transformed);
      } else {
        throw new Error(result.error || 'Failed to load invoices');
      }
    } catch (err) {
      console.error('Error loading invoices:', err);
      setError(err.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply filters to invoices
  const applyFilters = (invoiceList) => {
    let filtered = [...invoiceList];
    
    // Apply search filter (client-side for client name/email)
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(invoice => 
        invoice.invoiceNumber.toLowerCase().includes(searchTerm) ||
        invoice.clientName.toLowerCase().includes(searchTerm) ||
        (invoice.clientEmail && invoice.clientEmail.toLowerCase().includes(searchTerm))
      );
    }
    
    // Apply client filter
    if (filters.client) {
      filtered = filtered.filter(invoice => 
        invoice.clientName.toLowerCase().includes(filters.client.toLowerCase())
      );
    }
    
    // Apply amount filters
    if (filters.amountFrom) {
      filtered = filtered.filter(invoice => 
        invoice.totalAmount >= parseFloat(filters.amountFrom)
      );
    }
    
    if (filters.amountTo) {
      filtered = filtered.filter(invoice => 
        invoice.totalAmount <= parseFloat(filters.amountTo)
      );
    }
    
    setFilteredInvoices(filtered);
  };

  useEffect(() => {
    applyFilters(invoices);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, invoices]);

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleCreateInvoice = () => {
    setCreateModalOpen(true);
  };

  const handleSaveInvoice = async (invoiceData) => {
    try {
      // Ensure client_name is not null or empty
      const clientName = invoiceData.client_name || invoiceData.clientName;
      if (!clientName || clientName.trim() === '') {
        throw new Error('Client name is required. Please select a client.');
      }

      // Validate all required fields
      if (!invoiceData.amount || !invoiceData.total_amount) {
        throw new Error('Amount and total amount are required');
      }

      if (!invoiceData.due_date && !invoiceData.dueDate) {
        throw new Error('Due date is required');
      }

      const result = await invoiceService.createInvoice({
        client_name: clientName.trim(),
        client_email: (invoiceData.client_email || invoiceData.clientEmail || '').trim(),
        amount: parseFloat(invoiceData.amount),
        tax_amount: parseFloat(invoiceData.tax_amount || invoiceData.gstAmount || 0),
        total_amount: parseFloat(invoiceData.total_amount || invoiceData.totalAmount),
        issue_date: invoiceData.issue_date || invoiceData.invoiceDate,
        due_date: invoiceData.due_date || invoiceData.dueDate,
        status: invoiceData.status || 'draft',
        description: (invoiceData.description || '').trim()
      });

      if (result.success) {
        await loadInvoices();
        setCreateModalOpen(false);
        
        const successNotification = {
          id: Date.now(),
          type: 'success',
          title: 'Invoice Created',
          message: `Invoice ${result.data.invoice_number} has been created successfully`,
          timestamp: new Date()
        };
        
        setNotifications(prev => [successNotification, ...prev]);
      } else {
        throw new Error(result.error || 'Failed to create invoice');
      }
    } catch (err) {
      console.error('Error creating invoice:', err);
      setError(err.message || 'Failed to create invoice');
    }
  };

  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setDetailsModalOpen(true);
  };

  const handlePreviewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setPreviewModalOpen(true);
  };

  const handleEditInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setEditModalOpen(true);
  };

  const handleUpdateInvoice = async (invoiceData) => {
    if (!selectedInvoice) return;

    try {
      // Ensure client_name is not null or empty
      const clientName = invoiceData.client_name || invoiceData.clientName;
      if (!clientName || clientName.trim() === '') {
        throw new Error('Client name is required. Please select a client.');
      }

      // Validate all required fields
      if (!invoiceData.amount || !invoiceData.total_amount) {
        throw new Error('Amount and total amount are required');
      }

      if (!invoiceData.due_date && !invoiceData.dueDate) {
        throw new Error('Due date is required');
      }

      const result = await invoiceService.updateInvoice(selectedInvoice.id, {
        client_name: clientName.trim(),
        client_email: (invoiceData.client_email || invoiceData.clientEmail || '').trim(),
        amount: parseFloat(invoiceData.amount),
        tax_amount: parseFloat(invoiceData.tax_amount || invoiceData.gstAmount || 0),
        total_amount: parseFloat(invoiceData.total_amount || invoiceData.totalAmount),
        issue_date: invoiceData.issue_date || invoiceData.invoiceDate,
        due_date: invoiceData.due_date || invoiceData.dueDate,
        status: invoiceData.status || selectedInvoice.status || 'draft',
        description: (invoiceData.description || '').trim(),
        services: invoiceData.services || JSON.stringify([]) // Include services JSON
      });

      if (result.success) {
        await loadInvoices();
        setEditModalOpen(false);
        setSelectedInvoice(null);
        
        const successNotification = {
          id: Date.now(),
          type: 'success',
          title: 'Invoice Updated',
          message: `Invoice ${selectedInvoice.invoiceNumber} has been updated successfully`,
          timestamp: new Date()
        };
        
        setNotifications(prev => [successNotification, ...prev]);
      } else {
        throw new Error(result.error || 'Failed to update invoice');
      }
    } catch (err) {
      console.error('Error updating invoice:', err);
      setError(err.message || 'Failed to update invoice');
      
      const errorNotification = {
        id: Date.now(),
        type: 'error',
        title: 'Update Failed',
        message: err.message || 'Failed to update invoice',
        timestamp: new Date()
      };
      
      setNotifications(prev => [errorNotification, ...prev]);
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (window.confirm('Are you sure you want to delete this invoice?')) {
      try {
        const result = await invoiceService.deleteInvoice(invoiceId);
        
        if (result.success) {
          await loadInvoices();
          
          const deleteNotification = {
            id: Date.now(),
            type: 'info',
            title: 'Invoice Deleted',
            message: 'Invoice has been deleted successfully',
            timestamp: new Date()
          };
          
          setNotifications(prev => [deleteNotification, ...prev]);
        } else {
          throw new Error(result.error || 'Failed to delete invoice');
        }
      } catch (err) {
        console.error('Error deleting invoice:', err);
        setError(err.message || 'Failed to delete invoice');
      }
    }
  };

  const handleUpdateStatus = async (invoiceId, newStatus) => {
    try {
      const result = await invoiceService.updateInvoice(invoiceId, { status: newStatus });
      
      if (result.success) {
        await loadInvoices();
        
        const statusNotification = {
          id: Date.now(),
          type: 'success',
          title: 'Status Updated',
          message: `Invoice status updated to ${newStatus}`,
          timestamp: new Date()
        };
        
        setNotifications(prev => [statusNotification, ...prev]);
      } else {
        throw new Error(result.error || 'Failed to update status');
      }
    } catch (err) {
      console.error('Error updating status:', err);
      setError(err.message || 'Failed to update invoice status');
    }
  };

  const handleSendReminder = async (invoiceId, type) => {
    try {
      // Send invoice (updates status to 'sent')
      const result = await invoiceService.sendInvoice(invoiceId);
      
      if (result.success) {
        await loadInvoices();
        const invoice = invoices.find(inv => inv.id === invoiceId);
        
        const reminderNotification = {
          id: Date.now(),
          type: 'info',
          title: 'Reminder Sent',
          message: `${type === 'email' ? 'Email' : 'WhatsApp'} reminder sent to ${invoice?.clientName}`,
          timestamp: new Date()
        };
        
        setNotifications(prev => [reminderNotification, ...prev]);
      } else {
        throw new Error(result.error || 'Failed to send reminder');
      }
    } catch (err) {
      console.error('Error sending reminder:', err);
      setError(err.message || 'Failed to send reminder');
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      client: '',
      status: '',
      dateFrom: '',
      dateTo: '',
      amountFrom: '',
      amountTo: ''
    });
  };

  const handleDismissNotification = (notificationId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  };

  const viewTabs = [
    { id: 'invoices', label: 'Invoices', icon: 'FileText', count: invoices.length },
    { id: 'analytics', label: 'Analytics', icon: 'BarChart3', count: null }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <HeaderNavigation
        onSidebarToggle={handleSidebarToggle}
        user={userProfile || user}
        notifications={[]}
      />

      {/* Sidebar */}
      <MainSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      {/* Main Content */}
      <div className={`pt-16 transition-all duration-300 ${isCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        <div className="p-6">
          {/* Breadcrumb */}
          <BreadcrumbNavigation />

          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Invoice & Billing Management</h1>
              <p className="text-muted-foreground mt-2">
                Manage client invoices, track payments, and monitor billing analytics
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm">
                <Icon name="Download" size={16} className="mr-2" />
                Export Data
              </Button>
              <Button onClick={handleCreateInvoice}>
                <Icon name="Plus" size={16} className="mr-2" />
                Create Invoice
              </Button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-3">
                <div className="text-red-600">⚠️</div>
                <div>
                  <p className="text-red-800 font-medium">Error loading data</p>
                  <p className="text-red-600 text-sm">{error}</p>
                  <button 
                    onClick={loadInvoices}
                    className="text-red-700 underline text-sm mt-1"
                  >
                    Try again
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                <p className="text-muted-foreground text-sm">Loading invoices...</p>
              </div>
            </div>
          )}

          {/* View Tabs */}
          {!loading && (
            <>
              <div className="flex items-center gap-1 mb-6 bg-muted/30 p-1 rounded-lg w-fit">
                {viewTabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveView(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      activeView === tab.id
                        ? 'bg-card text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Icon name={tab.icon} size={16} />
                    {tab.label}
                    {tab.count !== null && (
                      <span className="bg-primary/10 text-primary text-xs px-2 py-0.5 rounded-full">
                        {invoices.length}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {/* Content based on active view */}
              {activeView === 'invoices' && (
                <div className="space-y-6">
                  {/* Filters */}
                  <InvoiceFilters
                    onFilterChange={handleFilterChange}
                    onClearFilters={handleClearFilters}
                  />

                  {/* Invoice Table */}
                  {filteredInvoices.length > 0 ? (
                    <InvoiceTable
                      invoices={filteredInvoices}
                      onViewInvoice={handleViewInvoice}
                      onEditInvoice={handleEditInvoice}
                      onDeleteInvoice={handleDeleteInvoice}
                      onPreviewInvoice={handlePreviewInvoice}
                      onUpdateStatus={handleUpdateStatus}
                    />
                  ) : (
                    <div className="text-center py-12">
                      <p className="text-muted-foreground">No invoices found</p>
                      <Button 
                        variant="outline" 
                        className="mt-4"
                        onClick={handleCreateInvoice}
                      >
                        Create First Invoice
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {activeView === 'analytics' && (
                <BillingAnalytics />
              )}
            </>
          )}
        </div>
      </div>

      {/* Modals */}
      <CreateInvoiceModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSave={handleSaveInvoice}
        isEditMode={false}
      />

      <CreateInvoiceModal
        isOpen={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedInvoice(null);
        }}
        onSave={handleUpdateInvoice}
        invoice={selectedInvoice}
        isEditMode={true}
      />

      <InvoiceDetailsModal
        isOpen={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        invoice={selectedInvoice}
        onUpdateStatus={handleUpdateStatus}
        onSendReminder={handleSendReminder}
      />

      <InvoicePreview
        isOpen={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        invoice={selectedInvoice}
      />

      {/* Notifications */}
      <NotificationToast
        notifications={notifications}
        onDismiss={handleDismissNotification}
        position="top-right"
        autoHideDuration={5000}
      />
    </div>
  );
};

export default InvoiceBillingManagement;